import type { ReactNode } from "react";
import {
  Box,
  Checkbox,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";

export type StepStatus = "pending" | "active" | "complete";

export interface ProgressStep {
  kind?: "step";
  key: string;
  label: string;
  /** Secondary caption under the label. */
  detail?: string;
  status: StepStatus;
}

/** Free text between steps (a plan's notes); never counted as a step. */
export interface ProgressNote {
  kind: "note";
  key: string;
  text: string;
}

export type ProgressItem = ProgressStep | ProgressNote;

const STEP_STATUS_LABEL: Record<StepStatus, string> = {
  pending: "Pending",
  active: "In progress",
  complete: "Complete",
};

const STEP_STATUS_COLOR: Record<StepStatus, "default" | "primary" | "success"> =
  {
    pending: "default",
    active: "primary",
    complete: "success",
  };

export interface StepProgressListProps {
  items: ProgressItem[];
  /** Noun inserted into the count line: "1 of 3 stages complete". */
  unit?: string;
  /** Rendered beside the count line (e.g. a verdict chip). */
  headerAside?: ReactNode;
  showPercent?: boolean;
  /** Per-row status chip; off when the checkbox alone tells the story. */
  showStatusChip?: boolean;
  /**
   * Strike through and mute completed labels instead of muting pending
   * ones: a to-do list fades what is done, a stage list fades what has not
   * started.
   */
  strikeCompleted?: boolean;
}

export const isProgressStep = (item: ProgressItem): item is ProgressStep =>
  item.kind !== "note";

/**
 * Progress header plus one row per step. Products compute their own steps;
 * this only renders them, so a status here never feeds back into a record.
 * Renders no header when there are no steps, so note-only content still
 * shows.
 */
const StepProgressList = ({
  items,
  unit,
  headerAside,
  showPercent = false,
  showStatusChip = false,
  strikeCompleted = false,
}: StepProgressListProps) => {
  const steps = items.filter(isProgressStep);
  const completed = steps.filter((step) => step.status === "complete").length;
  const total = steps.length;
  const progress = total === 0 ? 0 : (completed / total) * 100;

  return (
    <Box>
      {total > 0 && (
        <Box sx={{ mb: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 0.5 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {completed} of {total} {unit ? `${unit} ` : ""}complete
              </Typography>
              {headerAside}
            </Stack>
            {showPercent && (
              <Typography variant="caption" color="text.secondary">
                {Math.round(progress)}%
              </Typography>
            )}
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}
      <Stack spacing={0.5}>
        {items.map((item) =>
          isProgressStep(item) ? (
            <Stack
              key={item.key}
              direction="row"
              alignItems="flex-start"
              spacing={0.75}
              sx={{ py: 0.25 }}
            >
              <Checkbox
                checked={item.status === "complete"}
                disabled
                size="small"
                sx={{ p: 0.25 }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: item.status === "active" ? 600 : 400,
                    color: (
                      strikeCompleted
                        ? item.status === "complete"
                        : item.status === "pending"
                    )
                      ? "text.secondary"
                      : "text.primary",
                    textDecoration:
                      strikeCompleted && item.status === "complete"
                        ? "line-through"
                        : "none",
                  }}
                >
                  {item.label}
                </Typography>
                {item.detail && (
                  <Typography variant="caption" color="text.secondary">
                    {item.detail}
                  </Typography>
                )}
              </Box>
              {showStatusChip && (
                <Chip
                  size="small"
                  label={STEP_STATUS_LABEL[item.status]}
                  color={STEP_STATUS_COLOR[item.status]}
                  variant={item.status === "active" ? "filled" : "outlined"}
                />
              )}
            </Stack>
          ) : (
            <Typography
              key={item.key}
              variant="body2"
              color="text.secondary"
              sx={{ py: 0.25, pl: 0.5, whiteSpace: "pre-wrap" }}
            >
              {item.text}
            </Typography>
          ),
        )}
      </Stack>
    </Box>
  );
};

export default StepProgressList;
